import { useState, useRef, useEffect, type TouchEvent } from 'react';
import { type DebouncedFunc, debounce } from 'lodash';

type TouchEventHandler = (e: TouchEvent) => void;
type HandleMobileSemiScreenTouchStart = TouchEventHandler | undefined;
type HandleMobileSemiScreenTouchMove = DebouncedFunc<TouchEventHandler> | undefined;

export const useMobileSemiScreen = (
    isMobileSemiScreenType: boolean
): {
    isMobileSemiScreen: boolean;
    handleMobileSemiScreenTouchStart: HandleMobileSemiScreenTouchStart;
    handleMobileSemiScreenTouchMove: HandleMobileSemiScreenTouchMove;
} => {
    const [isMobileSemiScreen, setIsMobileSemiScreen] = useState(isMobileSemiScreenType);
    const touchStartY = useRef<number>(0);
    const hadMobileSemiScreen = useRef(false); // a modal should only show in semi-screen once
    const handleMobileSemiScreenTouchStart = useRef<HandleMobileSemiScreenTouchStart>(undefined);
    const handleMobileSemiScreenTouchMove = useRef<HandleMobileSemiScreenTouchMove>(undefined);

    useEffect(() => {
        if (
            isMobileSemiScreenType &&
            (!handleMobileSemiScreenTouchStart.current || !handleMobileSemiScreenTouchMove.current)
        ) {
            handleMobileSemiScreenTouchStart.current = (e: TouchEvent): void => {
                touchStartY.current = e.changedTouches[0].clientY;
            };
            handleMobileSemiScreenTouchMove.current = debounce((e: TouchEvent): void => {
                // if the user has dragged up more than 5px, hide the semi-screen
                if (touchStartY.current - e.changedTouches[0].clientY > 5) {
                    setIsMobileSemiScreen(false);
                }
            }, 100);
        }

        setIsMobileSemiScreen(!hadMobileSemiScreen.current);

        if (!hadMobileSemiScreen.current) {
            hadMobileSemiScreen.current = true;
        }

        return () => {
            handleMobileSemiScreenTouchMove.current?.cancel();
            handleMobileSemiScreenTouchMove.current = undefined;
            handleMobileSemiScreenTouchStart.current = undefined;
        };
    }, [isMobileSemiScreenType, isMobileSemiScreen]);

    return {
        isMobileSemiScreen,
        handleMobileSemiScreenTouchStart: handleMobileSemiScreenTouchStart.current,
        handleMobileSemiScreenTouchMove: handleMobileSemiScreenTouchMove.current,
    };
};
